import { Component, effect, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location, formatDate } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { map, Observable, pipe, startWith, Subscription, switchMap } from 'rxjs';
import { FormControl, FormGroup, Validators} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GeolocationService } from '@ng-web-apis/geolocation';
import { ToastrService } from 'ngx-toastr';
import { Coordinate } from '../shared/coordinate.model';
import { Catch } from '../shared/catch.model';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { AddCatchDialogComponent } from '../add-catch-dialog/add-catch-dialog.component';
import { CoordinateDialogComponent } from '../set-detail/coordinate-dialog/coordinate-dialog.component';
import { DateTimePickerComponent } from '../date-time-picker/date-time-picker.component';
import { Fishery, LocalStorageService, StorageResult } from '../shared/local-storage.service';
import { setHaulDateValidator, maxDateValidator, coordValidator, shapefileLatValidator, shapefileLonValidator, lookupItemValidator } from '../shared/trip-validator.model';
import { MatTable } from '@angular/material/table';
import { TrawlSet } from '../shared/trawl-set.model';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { Insights } from '../shared/insights.service';

@Component({
    selector: 'app-set-detail-trawl',
    templateUrl: './set-detail-trawl.component.html',
    styleUrls: ['./set-detail-trawl.component.css'],
    standalone: false
})
export class SetDetailTrawlComponent implements OnDestroy {

    /**
   * The is the fishing event (set) the component is detailing
   */ 
  fishingEvent: TrawlSet = new TrawlSet();
  prevSet: TrawlSet = new TrawlSet();
  setIndex!: number;
  isNewSet: boolean = false;
  isReadOnly: boolean = false;

  targetSpeciesList: LookupItem[] = [];
  filteredTargetSpeciesList!: Observable<LookupItem[]>;
  prioritySpeciesSubscription!: Subscription;
  prioritySpecies: LookupItem[] = [];
  netTypesList!: Observable<LookupItem[]>;

    /**
   * If -1, this set is not part of the landing, otherwise it's the index into the landing sets array
   */ 
    partOfLandingInd: number = -1;
  
    /**
     * If not -1 then the set is part of an edited logbook
     */
    partOfEditedLanding: number = -1;

    partOfDraftTrip: number = -1;

    draftSetId: string = '';

  // Catch table columns
  catchTableColumns: string[] = [
    'Species',
    'RetainedWeight',
    'RetainedCount',
    'ReleasedWeight',
    'ReleasedCount'
  ];
  retainedCountPresent: boolean = true;
  @ViewChild(MatTable) table!: MatTable<Catch>;

  /* Form Fields */
  setDetailForm: FormGroup = new FormGroup({
    netType: new FormControl<LookupItem>(new LookupItem(''), [Validators.required, lookupItemValidator()]),
    codendCapacity: new FormControl('', Validators.max(750000)),
    targetSpecies: new FormControl<LookupItem>(new LookupItem(''), [Validators.required, lookupItemValidator()]),
    startDate: new FormControl('', [Validators.required, maxDateValidator()]),
    endDate: new FormControl(''),
    startLat: new FormControl(new Coordinate, [Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLon, this.dataSync.west_coast_eez())]),
    startLon: new FormControl(new Coordinate, [Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLat, this.dataSync.west_coast_eez())]),
    endLat: new FormControl(new Coordinate, [coordValidator(), shapefileLatValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLon, this.dataSync.west_coast_eez())]),
    endLon: new FormControl(new Coordinate, [coordValidator(), shapefileLonValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLat, this.dataSync.west_coast_eez())]),
    averageDepth: new FormControl('', Validators.max(700)),
    comments: new FormControl(''),
    lostNet: new FormControl<boolean>(false)
  });

  // Subscriptions
  formUpdateSubscription: Subscription = new Subscription();
  locationSubscription: Subscription = new Subscription();
  netLostSubscription: Subscription = new Subscription();;
  userSetsSubscription!: Subscription;

  position!: GeolocationCoordinates;
  locationAllowed: boolean = false;

  previousScreen: string = 'menu';

  errorNumber: number = 0;

  constructor(
    public dataSync: LocalStorageService,
    public _location: Location,
    public router: Router,
    public route: ActivatedRoute,
    public dialog: MatDialog,
    private readonly geolocation$: GeolocationService,
    private insights: Insights,
    private toastr: ToastrService,
    private lookup: LookupDataService
  ) {
    this.setIndex = Number(this.route.snapshot.paramMap.get('index'));
      let editedIndex = +this.route.snapshot.queryParams.editedIndex;
      this.partOfDraftTrip = +(this.route.snapshot.queryParams.draftIndex ?? -1);

      this.route.params.subscribe(_ => this.initialize());
      this.locationSubscription = this.geolocation$.subscribe(pos => this.position = pos.coords, err => {

        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
        }
      });

      if (this.partOfDraftTrip) effect(() => {dataSync.trawl_drafts(); this.initialize()});
      else if (editedIndex > -1) effect(() => {dataSync.edited_trawlLogbook(); this.initialize()});
      else effect(() => {dataSync.user_trawlTrip(); this.initialize()});

      this.netTypesList = this.lookup.getNetTypesObservable();
      this.lookup.getTargetSpeciesObservable().subscribe((list: LookupItem[]) => this.targetSpeciesList = list); 
      this.filteredTargetSpeciesList = this.setDetailForm.get('targetSpecies')!.valueChanges.pipe(
        startWith(''),
        map(value => this.targetSpeciesList.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
      );   
      this.prioritySpeciesSubscription = this.lookup.getPrioritySpeciesObservable().subscribe((value) => this.prioritySpecies = value);
  }


  calculateErrors() {
    this.errorNumber = 0;
    for (const control in this.setDetailForm.controls)
      for (const err in this.setDetailForm.controls[control].errors)
        this.errorNumber++;
    if (this.setDetailForm.errors) this.errorNumber++;
  }

  initialize() {
    this.route.queryParams.subscribe(params => {
      // Disable the form if readOnly is set in the query params
      if (params['readOnly']) {
        this.setDetailForm.disable();
        this.isReadOnly = true;
      }
    });
    this.formUpdateSubscription.unsubscribe();
    this.netLostSubscription.unsubscribe();

    this.previousScreen = this.route.snapshot.queryParams.previous ?? 'menu';
    this.setIndex = Number(this.route.snapshot.paramMap.get('index'));
    this.isNewSet = this.route.snapshot.paramMap.get('isNew') === 'true';
    let completedInd = +this.route.snapshot.queryParams.completedIndex;

    // If not valid index, just go back
    if (this.dataSync.user_trawlTrip() && this.setIndex >= this.dataSync.user_trawlTrip().sets.length && isNaN(completedInd)) {
      this.router.navigate(['menu']);
      return;
    }

    if (this.setIndex > 0) this.prevSet = this.dataSync.user_trawlTrip().sets[this.setIndex -1];

    // Load up the fishingEvent
    let editedIndex = +this.route.snapshot.queryParams.editedIndex;
    this.partOfDraftTrip = +(this.route.snapshot.queryParams.draftIndex ?? -1);
    this.draftSetId = this.route.snapshot.queryParams.draftSetId;

    this.fishingEvent = editedIndex > -1 ? this.dataSync.edited_trawlLogbook().sets[editedIndex]
    : this.partOfDraftTrip > -1 ? this.dataSync.trawl_drafts()[this.partOfDraftTrip].sets.find(fs => fs.fishingEventID == this.draftSetId)!
        : completedInd > -1 ? this.dataSync.received_trawlLogbooks()[completedInd].sets[this.setIndex] : this.dataSync.user_trawlTrip()!.sets[this.setIndex];
    
    this.partOfLandingInd = this.dataSync.user_trawlTrip().trawlDelivery.sets.findIndex((s: TrawlSet) => s.fishingEventID === this.fishingEvent.fishingEventID);
    this.partOfEditedLanding = this.dataSync.edited_trawlLogbook().sets.findIndex((s: TrawlSet) => s.fishingEventID == this.fishingEvent.fishingEventID);
    
    // Set codendCapacity validator based on EM trip status
    const isEMTrip = this.dataSync.user_trawlTrip()?.trawlDelivery.isEMTrip;
    const validators = isEMTrip 
      ? [Validators.required, Validators.max(750000)]
      : [Validators.max(750000)];
    this.setDetailForm.get('codendCapacity')?.setValidators(validators);
    this.setDetailForm.get('codendCapacity')?.updateValueAndValidity();
    
    this.setDetailForm.patchValue({
      netType: this.fishingEvent.netType,
      codendCapacity: this.fishingEvent.codendCapacity,
      targetSpecies: this.fishingEvent.targetSpecies,
      startDate: this.fishingEvent.startDate
        ? formatDate(this.fishingEvent.startDate, 'EEEE, MMM d, y, HH:mm', 'en-US')
        : '',
      endDate: this.fishingEvent.endDate
        ? formatDate(this.fishingEvent.endDate, 'EEEE, MMM d, y, HH:mm', 'en-US')
        : '',
      startLat: this.fishingEvent.startLat,
      startLon: this.fishingEvent.startLon,
      endLat: this.fishingEvent.endLat,
      endLon: this.fishingEvent.endLon,
      averageDepth: this.fishingEvent.averageDepth,
      comments: this.fishingEvent.comments,
      lostNet: this.fishingEvent.lostNet
    });

    if (!this.fishingEvent.catchItems.some(ci => (ci.retainedCount ?? -1) >= 0)) {
      this.catchTableColumns = this.catchTableColumns.filter(ct => ct != 'RetainedCount')
      this.retainedCountPresent = false;
    }

    if (!this.fishingEvent.catchItems.some(ci => (ci.releasedCount ?? -1) >= 0))
      this.catchTableColumns = this.catchTableColumns.filter(ct => ct != 'ReleasedCount')

    this.subscribeToLostNetChanges();
    this.subscribeToFormUpdate();
    this.setDetailForm.get('lostNet')?.setValue(this.fishingEvent.lostNet);  
    this.setDetailForm.markAsPristine();
    this.calculateErrors();
  }


  subscribeToFormUpdate() {
    this.formUpdateSubscription = this.setDetailForm.valueChanges.subscribe(value => {
      if (this.setDetailForm.pristine) this.setDetailForm.markAsDirty();

      this.fishingEvent.startLatManualEntry ||= this.fishingEvent.startLat != value.startLat;
      this.fishingEvent.startLonManualEntry ||= this.fishingEvent.startLon != value.startLon;
      this.fishingEvent.endLatManualEntry ||= this.fishingEvent.endLat != value.endLat;
      this.fishingEvent.endLonManualEntry ||= this.fishingEvent.endLon != value.endLon;
      this.fishingEvent.update(value);

      // Write back to local storage
      this.updateLandingAndEvent();
      this.calculateErrors();
    });
  }


  private subscribeToLostNetChanges(): void {
    this.netLostSubscription = this.setDetailForm.get('lostNet')!.valueChanges.subscribe(lostNet => {
      if (lostNet) {
        // If lostNet is true, make endDate, endLat, endLon, and averageDepth not required.
        this.setDetailForm.get('endDate')?.setValidators([maxDateValidator(), setHaulDateValidator(this.fishingEvent.startDate)]);
        this.setDetailForm.get('endLat')?.setValidators([shapefileLatValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLon, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('endLon')?.setValidators([shapefileLonValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLat, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('averageDepth')?.setValidators(Validators.max(700));
      } else {
        // If lostNet is false, restore the validators.
        this.setDetailForm .get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.fishingEvent.startDate)]);
        this.setDetailForm.get('endLat')?.setValidators([coordValidator(), shapefileLatValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLon, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('endLon')?.setValidators([coordValidator(), shapefileLonValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLat, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('averageDepth')?.setValidators([Validators.required, Validators.max(700)]);
      }
  
      // Trigger validation update for these fields
      this.setDetailForm.get('endDate')?.updateValueAndValidity({emitEvent: false});
      this.setDetailForm.get('endLat')?.updateValueAndValidity({emitEvent: false});
      this.setDetailForm.get('endLon')?.updateValueAndValidity({emitEvent: false});
      this.setDetailForm.get('averageDepth')?.updateValueAndValidity();
      this.calculateErrors();
    });
  }

  /**
   * Database updates to current landing and this set.
   */
  updateLandingAndEvent() {
    if (this.setDetailForm.get('endDate')?.valid) {
      if (this.partOfLandingInd !== -1) {
        this.dataSync.user_trawlTrip.update(trip => {
          trip.trawlDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
          return trip;
        });
      } else {
        this.dataSync.user_trawlTrip.update(trip => {
          trip.trawlDelivery.sets.push(this.fishingEvent);
          return trip;
        });
        this.partOfLandingInd = this.dataSync.user_trawlTrip().trawlDelivery.sets.length - 1;
      }
    }

    if (this.partOfEditedLanding !== -1) {
      this.dataSync.edited_trawlLogbook.update(trip => {
        trip.sets[this.partOfEditedLanding] = this.fishingEvent;
        return trip;
      })
      this.dataSync.updateLanding(Fishery.Trawl, true, 'trawl').then((res: StorageResult) => {
        if (!res.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(res.error);
        }
      });
    }

    if (this.partOfDraftTrip !== -1) {
      this.dataSync.trawl_drafts.update(drafts => {
        drafts[this.partOfDraftTrip]
          .sets[drafts[this.partOfDraftTrip].sets.findIndex(fs => fs.fishingEventID == this.draftSetId)] = this.fishingEvent;
          return drafts;
      });
      
      this.dataSync.updateLanding(Fishery.Trawl, false, 'trawl', this.dataSync.trawl_drafts()[this.partOfDraftTrip].localLandingGUID, true).then((res: StorageResult) => {
        if (!res.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(res.error);
        }
      });
    }

    if (this.setIndex !== -1) this.dataSync.updateTrip("trawl").then((res: StorageResult) => {
      if (!res.success) {
        this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
        this.insights.trackException(res.error);
      }
    });
    
  }

  
  compareNetType(N1: LookupItem, N2: LookupItem): boolean {
    return N1.id == N2.id && N1.code == N2.code && N1.name === N2.name;
  }


  /**
   * For auto-filling of coordinates and date/time on the set.
   * We have to keep track of whether coordinate data is auto filled or manually entered.
   */
  fillSetTimeLocation() {
    if (!this.locationAllowed || !this.position) {
      this.locationSubscription = this.geolocation$.subscribe(pos => { this.position = pos.coords; this.locationAllowed = true; }, err => {

        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
          return;
        }
      });
    }

    let fillFunction =  () => {
      this.formUpdateSubscription.unsubscribe();
      this.setDetailForm.get('startLat')?.setValue(Coordinate.fillDegrees(this.position.latitude));
      this.setDetailForm.get('startLon')?.setValue(Coordinate.fillDegrees(this.position.longitude));
      this.setDetailForm.get('startDate')?.setValue(formatDate(new Date(), 'EEEE, MMM d, y, HH:mm', 'en-US'));
  
      this.fishingEvent.startLatManualEntry = false;
      this.fishingEvent.startLonManualEntry = false;
  
      this.fishingEvent.update(this.setDetailForm.value);

      if (this.partOfLandingInd != -1) {
        this.dataSync.user_trawlTrip.update(trip => {
          trip.trawlDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
          return trip;
        });
      }

      this.dataSync.updateTrip("trawl").then(result => {
        if (!result.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(result.error);
        }

        if (this.partOfEditedLanding != -1) {
          this.dataSync.edited_trawlLogbook.update(trip => {
            trip.sets[this.partOfEditedLanding] = this.fishingEvent;
            return trip;
          });
        }

        this.subscribeToFormUpdate();
        this.setDetailForm.get('startLat')?.setValidators([Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLon, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('startLat')?.updateValueAndValidity()
        this.setDetailForm.get('startLon')?.setValidators([Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry, this.fishingEvent.startLat, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('startLon')?.updateValueAndValidity();
        this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.setDetailForm.get('startDate')?.value)]);
        this.setDetailForm.get('endDate')?.updateValueAndValidity();
        this.calculateErrors();
      }, _ => {
        this.subscribeToFormUpdate();
        this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.setDetailForm.get('startDate')?.value)]);
        this.setDetailForm.get('endDate')?.updateValueAndValidity();
        this.calculateErrors();
      })
    };

    if (this.fishingEvent.startLat.isUnfilled() && this.fishingEvent.startLon.isUnfilled() && !this.fishingEvent.startDate) {
      fillFunction();
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      data: {screen: 'autofill'}
    }).afterClosed().subscribe(result => {
      if (result) fillFunction();
    });   
  }

  /**
   * For auto-filling of coordinates and date/time on the haul portion.
   * We have to keep track of whether coordinate data is auto filled or manually entered.
   */
  fillHaulTimeLocation() {
    if (!this.locationAllowed || this.position) {
      this.locationSubscription = this.geolocation$.subscribe(pos => { this.position = pos.coords; this.locationAllowed = true; }, err => {

        // User has denied permission
        if (err.code == 1) {
          this.toastr.warning('Allow geolocation permission to access device location', 'Location Permission Denied');
          this.locationAllowed = false;
        }
      });
    }

    let fillFunction = () => {
      this.formUpdateSubscription.unsubscribe();

      this.setDetailForm.get('endLat')?.setValue(Coordinate.fillDegrees(this.position.latitude));
      this.setDetailForm.get('endLon')?.setValue(Coordinate.fillDegrees(this.position.longitude));
      this.setDetailForm.get('endDate')?.setValue(formatDate(new Date(), 'EEEE, MMM d, y, HH:mm', 'en-US'));
      this.fishingEvent.endLatManualEntry = false;
      this.fishingEvent.endLonManualEntry = false;
      this.fishingEvent.update(this.setDetailForm.value);

      if (this.partOfLandingInd != -1) {
        this.dataSync.user_trawlTrip.update(trip => {
          trip.trawlDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
          return trip;
        });
      }
      this.dataSync.updateTrip("trawl").then(result => {
        if (!result.success) {
          this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
          this.insights.trackException(result.error);
        }
        if (this.partOfEditedLanding != -1) {
          this.dataSync.edited_trawlLogbook.update(trip => {
            trip.sets[this.partOfEditedLanding] = this.fishingEvent;
            return trip;
          });
        }

        this.setDetailForm.get('endLat')?.setValidators([Validators.required, coordValidator(), shapefileLatValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLon, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('endLat')?.updateValueAndValidity()
        this.setDetailForm.get('endLon')?.setValidators([Validators.required, coordValidator(), shapefileLonValidator(this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry, this.fishingEvent.endLat, this.dataSync.west_coast_eez())]);
        this.setDetailForm.get('endLon')?.updateValueAndValidity();
        this.subscribeToFormUpdate();
        this.setDetailForm.updateValueAndValidity();
        this.calculateErrors();
      }, _ => {
        this.subscribeToFormUpdate()
        this.setDetailForm.updateValueAndValidity();
        this.calculateErrors();
      });
    }

    if (this.fishingEvent.endLat.isUnfilled() && this.fishingEvent.endLon.isUnfilled()) {
      fillFunction();
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      data: {screen: 'autofill'}
    }).afterClosed().subscribe(result => {
      if (result) fillFunction();
    });
  }

    /**
   * Copy strategy / net-type / codend settings from the previous set.
   */
  copyFromPreviousSet(): void {
    // Only act when we actually have a previous set.
    if (!this.prevSet || this.setIndex === 0) return;

    this.formUpdateSubscription.unsubscribe();

    // Patch the form controls 
    this.setDetailForm.patchValue({
      netType:        this.prevSet.netType,
      codendCapacity: this.prevSet.codendCapacity,
      targetSpecies:  this.prevSet.targetSpecies
    });
    this.setDetailForm.markAsDirty();

    this.fishingEvent.netType        = this.prevSet.netType;
    this.fishingEvent.codendCapacity = this.prevSet.codendCapacity;
    this.fishingEvent.targetSpecies = this.prevSet.targetSpecies;

    this.updateLandingAndEvent();
    this.subscribeToFormUpdate();
    this.calculateErrors();
  }


  /**
   * Manual date/time editing through the DateTimePickerComponent
   * @param startOrEnd Start or end date
   */
  editDateTime(startOrEnd: string) {
    let dateInput = this.setDetailForm.get(startOrEnd)?.value == '' ? new Date() : new Date(this.setDetailForm.get(startOrEnd)?.value);
    let start: boolean = startOrEnd.toLowerCase() == "startdate";

    const dialogRef = this.dialog.open(DateTimePickerComponent, {
      width: '500px',
      autoFocus: false,
      data: { date: dateInput, isDelivery: false, isHMS: false, isStart: start, isEnd: !start },
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        //Adjusting the validation here gives a pouchDB update conflict since a call to the update subscription gets triggered twice
        this.formUpdateSubscription.unsubscribe();
        this.setDetailForm.get(startOrEnd)?.setValue(formatDate(result, 'EEEE, MMM d, y, HH:mm', 'en-US'));
        this.subscribeToFormUpdate();
        this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.setDetailForm.get('startDate')?.value)]);
        this.setDetailForm.get('endDate')?.updateValueAndValidity();
        this.calculateErrors();
      }
    });
  }

  // Coordinate entry dialog
  openCoordinateDialog(controlName: string) {
    let currentCoord: Coordinate = this.setDetailForm.get(controlName)?.value;
    const dialogRef = this.dialog.open(CoordinateDialogComponent, {
      width: '400px',
      data: { title: controlName, coord: currentCoord, fishery: 'GF' }
    });
    
    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        console.log(result)
        const isManual = !result.isUnfilled();
        switch (controlName) {
          case 'startLat':
            this.fishingEvent.startLatManualEntry = isManual;
            break;
          case 'startLon':
            this.fishingEvent.startLonManualEntry = isManual;
            break;
          case 'endLat':
            this.fishingEvent.endLatManualEntry = isManual;
            break;
          case 'endLon':
            this.fishingEvent.endLonManualEntry = isManual;
            break;
        }
        currentCoord = result;
        if (isManual) currentCoord.fillDecimal();
        this.formUpdateSubscription.unsubscribe();
        this.setDetailForm.patchValue({ [controlName]: result });
        this.fishingEvent.update(this.setDetailForm.value);
        this.subscribeToFormUpdate();

        // Rebuild the shapefile validators so the closure captures the now-true manual flag.
        const startManual = this.fishingEvent.startLatManualEntry || this.fishingEvent.startLonManualEntry;
        const endManual = this.fishingEvent.endLatManualEntry || this.fishingEvent.endLonManualEntry;
        const lostNet = this.setDetailForm.get('lostNet')?.value;
        if (controlName === 'startLat' || controlName === 'startLon') {
          this.setDetailForm.get('startLat')?.setValidators([Validators.required, coordValidator(), shapefileLatValidator(startManual, this.fishingEvent.startLon, this.dataSync.west_coast_eez())]);
          this.setDetailForm.get('startLon')?.setValidators([Validators.required, coordValidator(), shapefileLonValidator(startManual, this.fishingEvent.startLat, this.dataSync.west_coast_eez())]);
          this.setDetailForm.get('startLat')?.updateValueAndValidity();
          this.setDetailForm.get('startLon')?.updateValueAndValidity();
        } else {
          const endLatValidators = lostNet
            ? [shapefileLatValidator(endManual, this.fishingEvent.endLon, this.dataSync.west_coast_eez())]
            : [coordValidator(), shapefileLatValidator(endManual, this.fishingEvent.endLon, this.dataSync.west_coast_eez())];
          const endLonValidators = lostNet
            ? [shapefileLonValidator(endManual, this.fishingEvent.endLat, this.dataSync.west_coast_eez())]
            : [coordValidator(), shapefileLonValidator(endManual, this.fishingEvent.endLat, this.dataSync.west_coast_eez())];
          this.setDetailForm.get('endLat')?.setValidators(endLatValidators);
          this.setDetailForm.get('endLon')?.setValidators(endLonValidators);
          this.setDetailForm.get('endLat')?.updateValueAndValidity();
          this.setDetailForm.get('endLon')?.updateValueAndValidity();
        }

        this.setDetailForm.get(controlName)?.updateValueAndValidity();
        this.calculateErrors();
      }     
    });
  }

  /**
   * Open the catch dialog to add fish species
   */ 
  openCatchDialog(catchIndex: number): void {
    if (this.isReadOnly) return;

    let isNew = false;
    if (catchIndex >= this.fishingEvent.catchItems.length) {
      this.fishingEvent.catchItems.push(new Catch(''));
      isNew = true;
    }

    let isEMTrip = this.partOfEditedLanding != -1 ? this.dataSync.edited_trawlLogbook().isEMTrip : 
    this.dataSync.user_trawlTrip().trawlDelivery.isEMTrip;

    const dialogRef = this.dialog.open(AddCatchDialogComponent, {
      autoFocus: false,
      width: '400px',
      height: '600px',
      position: {top: '5px'},
      disableClose: true,
      data: {catch: this.fishingEvent.catchItems[catchIndex], fishery: 'Trawl', EMTrip: isEMTrip, prioritySpecies: this.prioritySpecies} //empty if row clicked
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.delete) {
          this.fishingEvent.catchItems.splice(catchIndex, 1);
        } else {
          if (catchIndex >= this.fishingEvent.catchItems.length) this.fishingEvent.catchItems.push(new Catch(''));
          let item = this.fishingEvent.catchItems[catchIndex];
          item.species = result.species;
          item.retainedCount = result.retainedCount ? Number.parseInt(result.retainedCount) : undefined;
          item.retainedWeight = Number.parseInt(result.retainedWeight);
          item.retainedWeight = item.retainedWeight ? item.retainedWeight : 0;
          item.releasedCount = result.releasedCount ? Number.parseInt(result.releasedCount) : undefined;
          item.releasedWeight = result.releasedWeight ? Number.parseInt(result.releasedWeight) : undefined;
        }

        if (this.fishingEvent.catchItems.some(ci => (ci.retainedCount ?? -1) >= 0)) {
          if (!this.retainedCountPresent) this.catchTableColumns.splice(2, 0, 'RetainedCount');
          this.retainedCountPresent = true;
        } else {
          this.catchTableColumns = this.catchTableColumns.filter(ct => ct != 'RetainedCount')
          this.retainedCountPresent = false;
        }

        if (this.fishingEvent.catchItems.some(ci => (ci.releasedCount ?? -1) >= 0)) {
          if (!this.catchTableColumns.includes('ReleasedCount')) this.catchTableColumns.push('ReleasedCount');
        } else {
          this.catchTableColumns = this.catchTableColumns.filter(ct => ct != 'ReleasedCount')
        }
        this.table.renderRows();

        if (this.partOfLandingInd != -1) {
          this.dataSync.user_trawlTrip.update(trip => {
            trip.trawlDelivery.sets[this.partOfLandingInd] = this.fishingEvent;
            return trip;
          });
        }
        if (this.partOfEditedLanding != -1) {
          this.dataSync.edited_trawlLogbook.update(trip => {
            trip.sets[this.partOfEditedLanding] = this.fishingEvent;
            return trip;
          })
          this.updateLandingAndEvent();
        }
        this.dataSync.updateTrip("trawl").then((res: StorageResult) => {
          if (!res.success) {
            this.toastr.error('Entered data has not been saved. Please re-enter your data.', 'Trip Update Error');
            this.insights.trackException(res.error);
          }
        });
      } else if (isNew) {
        this.fishingEvent.catchItems.splice(catchIndex, 1);
      }
      
    });
  }


  /**
   * Highlight fields that are in an error state.
   */
  openErrorDialog() {   
    //Looks like adding/removing validators is the solution here, need to finesse a bit though since this adds but doesn't remove
    this.setDetailForm.get('endDate')?.setValidators([Validators.required, maxDateValidator(), setHaulDateValidator(this.fishingEvent.startDate)]);
    this.setDetailForm.get('endDate')?.updateValueAndValidity();
    this.setDetailForm.markAllAsTouched();
    console.log(this.setDetailForm.errors)
    if (this.setDetailForm.valid)
      this.toastr.success('No errors on this set', 'Valid');
    else
      this.toastr.warning('Errors on this set, see fields', 'Invalid');
  }

  // Navigation & deletion
  confirmDelete() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: { screen: 'deleteset' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteSet();
      }
    });
  }

  /**
   * Delete the current set
   */ 
  async deleteSet(): Promise<boolean> {
    // If no valid set exists, resolve immediately.
    if (this.setIndex === -1 && this.partOfEditedLanding === -1) {
      return Promise.resolve(true);
    }

    const res = await this.dataSync.deleteFishingEvent(this.fishingEvent.fishingEventID, this.partOfEditedLanding !== -1, "trawl");
    if (res) {
      console.log("going to menu");
      if (this.partOfEditedLanding !== -1) {
        this.navigateToDelivery();
      } else {
        this.router.navigate(['menu']);
      }
      return true;
    } else {
      alert('Deletion failed');
      return false;
    }
  }

  previousSet(): void {
    const sorted = this.getSortedSets();
    const currentPos = sorted.findIndex(
      s => s.fishingEventID === this.fishingEvent.fishingEventID
    );
    if (currentPos > 0) {
      const targetId   = sorted[currentPos - 1].fishingEventID;
      const targetIdx  = this.dataSync.user_trawlTrip()!.sets.findIndex(s => s.fishingEventID === targetId);
      this.router.navigate(['set_detail_trawl', targetIdx, false]);
    }
  }
  

  addSet() {
    if (!this.dataSync.storage_open) return;
  
    let newSet = new TrawlSet();
    this.dataSync.storeFishingEvent(newSet, 'trawl')
      .then((res: StorageResult) => {
        if (res.success) {
          this.partOfLandingInd = -1;
          this.partOfEditedLanding = -1;
          const newIndex = this.dataSync.user_trawlTrip().sets.findIndex(s => s.fishingEventID === res.data) ?? -1;  
          this.router.navigate(['set_detail_trawl', newIndex, true]);
          
          if ((document.documentElement.scrollTop || document.body.scrollTop) > 0) {
            window.scrollTo(0, 0);
          }
          this.setDetailForm.markAsUntouched();
        } else {
          this.toastr.error('Failed to add new Trawl set', 'Error');
          this.insights.trackException(res.error);
        }
        
      })
      .catch(_ => {
        console.error('Failed to add new Trawl set');
      });
  }  


  nextSet(): void {
    const sorted = this.getSortedSets();
    const currentPos = sorted.findIndex(
      s => s.fishingEventID === this.fishingEvent.fishingEventID
    );
    if (currentPos !== -1 && currentPos < sorted.length - 1) {
      const targetId   = sorted[currentPos + 1].fishingEventID;
      const targetIdx  = this.dataSync.user_trawlTrip()!.sets.findIndex(s => s.fishingEventID === targetId);
      this.router.navigate(['set_detail_trawl', targetIdx, false]);
    }
  }
  
    /** Return a shallow, date-sorted copy of the sets array. 
     *  Matches the ordering that the main menu has
     **/
  private getSortedSets(): TrawlSet[] {
    return [...this.dataSync.user_trawlTrip()!.sets].sort((a, b) =>
      (a.startDate ? new Date(a.startDate).getTime() : Number.MIN_SAFE_INTEGER) -
      (b.startDate ? new Date(b.startDate).getTime() : Number.MIN_SAFE_INTEGER)
    );
  }


  navigateToDelivery() {
    // Check if this is a new, unchanged set and delete it before navigating
    if (this.isNewSet && this.setDetailForm.pristine) {
      this.deleteSet().then((res) => {
        if (res) {
          // Navigate to delivery after successful deletion
          if (this.partOfDraftTrip != -1) {
            this.router.navigate(['trawl_delivery', 3, this.dataSync.trawl_drafts().findIndex(rl => rl.sets.some(s => s.fishingEventID == this.fishingEvent.fishingEventID))], { queryParams: { previous: 'trawl_drafts', setIndex: this.partOfDraftTrip } });
          } else if (this.setIndex == -1 && this.partOfEditedLanding != -1) {
            this.router.navigate(['trawl_delivery', 2, this.dataSync.received_trawlLogbooks().findIndex(rl => rl.sets.some(s => s.fishingEventID == this.fishingEvent.fishingEventID))], { queryParams: { previous: 'set_detail_trawl', setIndex: this.setIndex } })
          } else if (this.setIndex != -1) {
            this.router.navigate(['trawl_delivery', 0, -1], { queryParams: { previous: 'set_detail_trawl', setIndex: this.setIndex } });
          }
        }
      });
    } else {
      // If not a new set or it has been modified, just navigate
      if (this.partOfDraftTrip != -1) {
        this.router.navigate(['trawl_delivery', 3, this.dataSync.trawl_drafts().findIndex(rl => rl.sets.some(s => s.fishingEventID == this.fishingEvent.fishingEventID))], { queryParams: { previous: 'trawl_drafts', setIndex: this.partOfDraftTrip } });
      } else if (this.setIndex == -1 && this.partOfEditedLanding != -1) {
        this.router.navigate(['trawl_delivery', 2, this.dataSync.received_trawlLogbooks().findIndex(rl => rl.sets.some(s => s.fishingEventID == this.fishingEvent.fishingEventID))], { queryParams: { previous: 'set_detail_trawl', setIndex: this.setIndex } })
      } else if (this.setIndex != -1) {
        this.router.navigate(['trawl_delivery', 0, -1], { queryParams: { previous: 'set_detail_trawl', setIndex: this.setIndex } });
      }
    }
  }

  /**
 * Checks if the set is new and untouched, and deletes it if so before navigating away.
 * @param targetRoute The route to navigate to (e.g., 'menu' or home).
 */
  handleExit(targetRoute: string) {
    if (this.isNewSet && this.setDetailForm.pristine) {
      // Call deleteSet (navigation done within deleteSet)
      this.deleteSet().then((res) => {
        if (res) {
          console.log('New, untouched set deleted before navigation.');
        } else {
          // If deletion failed, you may wish to still navigate or alert the user.
          console.error('Deletion failed.');
        }
      });
    } else {
      if (targetRoute == 'trawl_delivery') {
        this.navigateToDelivery();
        return;
      }
      // If the form has been touched (or it’s an existing set), just navigate.
      this.router.navigate([targetRoute]);
    }
  }

  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
    if (this.locationSubscription) this.locationSubscription.unsubscribe();
    if (this.userSetsSubscription) this.userSetsSubscription.unsubscribe();
    if (this.netLostSubscription) this.netLostSubscription.unsubscribe();
    if (this.prioritySpeciesSubscription != null) this.prioritySpeciesSubscription.unsubscribe();
  }
}
